import React, { useState, useEffect, useCallback } from 'react';
import BIMViewer from './BimViewer';
import api from './api';
import toast, { Toaster } from 'react-hot-toast';
import { CloudUpload, Box, Cpu, FileText, PlusCircle, Layers, ListTree, Loader2, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { AuthProvider, useAuth } from './AuthContext';
import LoginPage from './LoginPage';
import SettingsPage from './SettingsPage';

// =================================================================
// 🎯 Component that renders the hierarchy tree
// =================================================================
const TreeNode = ({ node }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="ml-3 mt-1 select-none">
      <div
        onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) setExpanded(!expanded);
            else window.selectBIMElement?.(node.id);
        }}
        className="flex items-center gap-2 cursor-pointer hover:text-blue-600 text-xs text-slate-700 py-1 transition-colors"
      >
         <span className="text-[10px] w-3 text-center text-slate-400">
           {hasChildren ? (expanded ? '▼' : '▶') : '🔹'}
         </span>
         <span className={`truncate ${!hasChildren && 'font-medium'}`}>{node.name}</span>
      </div>
      {expanded && hasChildren && (
         <div className="border-l border-slate-200 ml-1.5 pl-1">
           {node.children.map(c => <TreeNode key={c.id} node={c} />)}
         </div>
      )}
    </div>
  );
};

// =================================================================
// 🎯 Waits until the model translation actually finishes before we try to open it in the viewer
// =================================================================
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 90;

async function waitForTranslation(urn, onProgress) {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    try {
      const res = await api.get(`/v1/aps/manifest/${urn}`);
      const { status, progress } = res.data;
      if (status === 'success') return;
      if (status === 'failed') {
        throw new Error('Model translation failed in Autodesk.');
      }
      if (onProgress) onProgress(progress || status);
    } catch (err) {
      if (err.message && err.message.includes('translation failed')) throw err;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
  throw new Error('Model translation took longer than expected, please try again shortly.');
}

// =================================================================
// Main viewer screen (requires login)
// =================================================================
function ViewerApp({ onOpenSettings, showSettingsButton }) {
  const { user, logout } = useAuth();
  const [urn, setUrn] = useState(null);
  const [models, setModels] = useState([]);
  const [showUpload, setShowUpload] = useState(true);
  const [hierarchy, setHierarchy] = useState(null);
  const [activeTab, setActiveTab] = useState('models');

  const fetchModels = async () => {
    try {
      const res = await api.get('/v1/aps/models');
      setModels(res.data);
    } catch (err) {
      console.error("Error fetching models:", err);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const toastId = toast.loading('Uploading file...');

    try {
      const uploadRes = await api.post('/v1/aps/upload', formData);
      const uploadedUrn = uploadRes.data.urn;

      toast.loading('Upload complete, building the Digital Twin (may take a few minutes depending on file size)...', { id: toastId });

      await waitForTranslation(uploadedUrn, (progress) => {
        toast.loading(`Building the Digital Twin... ${progress}`, { id: toastId });
      });

      toast.success('Model prepared successfully!', { id: toastId });
      setUrn(uploadedUrn);
      setShowUpload(false);
      setActiveTab('hierarchy');
      fetchModels();
    } catch (err) {
      console.error('Upload/translation error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'A problem occurred while uploading or preparing the model.';
      toast.error(errorMsg, { id: toastId, duration: 8000 });
    }
  };

  const handleTreeReady = useCallback((tree) => {
      setHierarchy(tree);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col" dir="ltr">
      <header className="bg-slate-900 text-white p-4 shadow-md border-b-4 border-blue-500 flex justify-between items-center z-10">
        <div className="flex items-center gap-3">
          <Cpu className="text-blue-400" size={28} />
          <h1 className="text-xl font-black tracking-wider m-0">
            SAP to BIM <span className="text-slate-400 font-medium">| Integration Portal</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-300 hidden sm:inline">
            {user?.full_name || user?.username} <span className="text-slate-500">({user?.role})</span>
          </span>
          {showSettingsButton && (
            <button onClick={onOpenSettings} className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" title="Settings">
              <SettingsIcon size={16} />
            </button>
          )}
          <button onClick={logout} className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" title="Logout">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">

        <aside className="w-80 bg-white border-r border-slate-200 flex flex-col shadow-sm z-0">
          <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <span className="font-bold text-slate-700 flex items-center gap-2 text-xs uppercase tracking-wider">
              <Layers size={16} className="text-blue-500" />
              Project Explorer
            </span>
            <button
              onClick={() => { setUrn(null); setShowUpload(true); setHierarchy(null); setActiveTab('models'); }}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1 text-xs font-bold border border-blue-100"
              title="Upload New Model"
            >
              <PlusCircle size={14} />
              New
            </button>
          </div>

          <div className="flex border-b border-slate-200">
            <button onClick={() => setActiveTab('models')} className={`flex-1 py-2 text-xs font-bold flex items-center justify-center gap-2 ${activeTab === 'models' ? 'border-b-2 border-blue-600 text-blue-600 bg-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              <Layers size={14} /> Models
            </button>
            <button onClick={() => setActiveTab('hierarchy')} disabled={!urn} className={`flex-1 py-2 text-xs font-bold flex items-center justify-center gap-2 ${activeTab === 'hierarchy' ? 'border-b-2 border-blue-600 text-blue-600 bg-white' : 'text-slate-500 hover:bg-slate-50'} disabled:opacity-50 disabled:cursor-not-allowed`}>
              <ListTree size={14} /> Hierarchy
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {activeTab === 'models' ? (
              models.length === 0 ? (
                <div className="text-center text-slate-400 text-xs py-8 italic">No models found in bucket</div>
              ) : (
                models.map((model) => (
                  <button
                    key={model.urn}
                    onClick={() => { setUrn(model.urn); setShowUpload(false); setHierarchy(null); setActiveTab('hierarchy'); }}
                    className={`w-full text-left p-3 rounded-xl flex items-start gap-3 transition-all border ${
                      urn === model.urn && !showUpload
                        ? 'bg-blue-50/60 border-blue-200 text-blue-700 shadow-sm font-medium'
                        : 'hover:bg-slate-50 border-transparent text-slate-600'
                    }`}
                  >
                    <FileText className={`shrink-0 mt-0.5 ${urn === model.urn && !showUpload ? 'text-blue-600' : 'text-slate-400'}`} size={18} />
                    <div className="overflow-hidden">
                      <p className="text-xs font-bold truncate mb-0.5">{model.filename}</p>
                      <p className="text-[10px] text-slate-400">Size: {(model.size / (1024 * 1024)).toFixed(2)} MB</p>
                    </div>
                  </button>
                ))
              )
            ) : (
              <div className="h-full px-1">
                {!hierarchy ? (
                  <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                    <Loader2 size={24} className="animate-spin mb-2" />
                    <span className="text-xs">Extracting Hierarchy...</span>
                  </div>
                ) : (
                  <TreeNode node={hierarchy} />
                )}
              </div>
            )}
          </div>
        </aside>

        <main className="flex-1 p-6 overflow-y-auto bg-slate-50">

          {showUpload && (
            <div className="max-w-3xl mx-auto my-12 flex flex-col items-center justify-center p-16 border-2 border-dashed border-slate-300 bg-white rounded-2xl shadow-sm hover:border-blue-500 hover:bg-blue-50/30 transition-all duration-300">
              <CloudUpload size={56} className="text-blue-500 mb-4" />
              <h2 className="text-xl font-bold text-slate-700 mb-1">Upload Revit Model</h2>
              <p className="text-xs text-slate-500 mb-6">Select a .rvt file to add it to your Autodesk bucket</p>

              <input
                type="file"
                accept=".rvt"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-bold cursor-pointer transition-colors shadow-md shadow-blue-200 flex items-center gap-2 text-sm"
              >
                <Box size={16} />
                Browse Files
              </label>
            </div>
          )}

          {!showUpload && urn && (
            <div className="bg-white p-4 rounded-2xl shadow-md border border-slate-200/60 flex flex-col h-full min-h-[650px]">
              <div className="flex justify-between items-center mb-4 px-1">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Box className="text-blue-500" size={20} />
                  Digital Twin Model Viewer
                </h3>
              </div>
              <div className="flex-1 min-h-0">
                <BIMViewer urn={urn} onTreeReady={handleTreeReady} />
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}

// =================================================================
// Decides what to render: loading, login, settings, or the viewer -- based on login state
// =================================================================
function AppShell() {
  const { user, isReady } = useAuth();
  const [view, setView] = useState('viewer');

  // 🎯 The Toaster must be mounted once here at the top, outside any
  // conditional rendering. It used to be mounted only inside ViewerApp, so
  // when the Settings screen opened (which fully replaces ViewerApp) there
  // was no Toaster mounted at all, and the "test connection" toasts had no
  // stable place to render properly.
  return (
    <>
      <Toaster position="top-center" reverseOrder={false} toastOptions={{ style: { zIndex: 99999 } }} containerStyle={{ zIndex: 99999 }} />
      {renderView()}
    </>
  );

  function renderView() {
    if (!isReady) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <Loader2 className="animate-spin text-blue-600" size={32} />
        </div>
      );
    }

    if (!user) {
      return <LoginPage />;
    }

    if (view === 'settings' && user.role === 'admin') {
      return <SettingsPage onBack={() => setView('viewer')} />;
    }

    return (
      <ViewerApp
        onOpenSettings={() => setView('settings')}
        showSettingsButton={user.role === 'admin'}
      />
    );
  }
}

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
